/**
 * V2QsOnsetSimple 컴포넌트
 *
 * V1 QsInputOnset과 동일한 UI:
 * - 날짜 선택기 + 시간 입력 (2일 이내일 때)
 * - "X hours" 또는 "X days" 차이 표시
 *
 * onset_v2 타입용 V2QsOnset과 달리 프리셋 버튼 없이 바로 날짜/시간 입력
 */

import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { differenceInCalendarDays, differenceInHours, differenceInMinutes } from 'date-fns';

export interface V2QsOnsetSimpleProps {
  /** 현재 값 (YYYY-MM-DD HH:mm 형식) */
  value?: string;
  /** 값 변경 핸들러 */
  onChange?: (value: string) => void;
  /** 에러 상태 */
  error?: boolean;
  /** 에러 메시지 */
  errorMessage?: string;
  /** 비활성화 상태 */
  disabled?: boolean;
  /** 전체 너비 사용 */
  fullWidth?: boolean;
  /** 추가 클래스 */
  className?: string;
  /** 라벨 */
  label?: string;
  /** 힌트 텍스트 */
  hint?: string;
  /** 최대 날짜 (기본: 오늘) */
  maxDate?: Date;
}

/**
 * 날짜/시간 문자열 파싱
 */
function parseDateTimeString(value?: string): { date: string; time: string } {
  if (!value) return { date: '', time: '' };

  const datePart = value.substring(0, 10);
  const timePart = value.substring(11, 16);

  return {
    date: datePart.length === 10 ? datePart : '',
    time: timePart.length >= 5 ? timePart : '',
  };
}

function V2QsOnsetSimple({
  value,
  onChange,
  error = false,
  errorMessage,
  disabled = false,
  fullWidth = true,
  className,
  label,
  hint,
  maxDate,
}: V2QsOnsetSimpleProps) {
  const { date: initDate, time: initTime } = parseDateTimeString(value);

  const [dateValue, setDateValue] = useState(initDate);
  const [timeValue, setTimeValue] = useState(initTime);

  const finalValue = dateValue ? `${dateValue} ${timeValue}` : '';

  // 외부 value 변경 시 동기화
  useEffect(() => {
    const { date, time } = parseDateTimeString(value);
    if (date !== dateValue) setDateValue(date);
    if (time !== timeValue) setTimeValue(time);
  }, [value]);

  // 값 변경 시 onChange 호출
  useEffect(() => {
    if (dateValue && timeValue) {
      if (finalValue !== value) {
        onChange?.(finalValue);
      }
    }
  }, [dateValue, timeValue, finalValue, value, onChange]);

  // 오늘과의 날짜/시간 차이 계산
  const { daysDiff, hourDiff, diffMins } = useMemo(() => {
    if (!dateValue) {
      return { daysDiff: 0, hourDiff: 0, diffMins: 0 };
    }

    const now = new Date();
    const selectedDate = new Date(dateValue);
    const days = differenceInCalendarDays(now, selectedDate);

    let hours = 0;
    let mins = 0;
    if (finalValue && timeValue) {
      const fullDateTime = new Date(`${dateValue}T${timeValue}:00`);
      hours = differenceInHours(now, fullDateTime);
      mins = differenceInMinutes(now, fullDateTime);
    }

    return { daysDiff: days, hourDiff: hours, diffMins: mins };
  }, [dateValue, timeValue, finalValue]);

  // 시간 입력 필요 여부 (2일 이내)
  const needTimeInput = dateValue && daysDiff < 2;

  // 미래 시간인지 체크
  const isFuture = !!timeValue && !!dateValue && diffMins < 1;

  // 날짜 변경 핸들러
  const handleDateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDateValue(e.target.value);
    },
    []
  );

  // 시간 변경 핸들러
  const handleTimeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setTimeValue(e.target.value);
    },
    []
  );

  const hasError = error || isFuture;
  const maxDateStr = maxDate
    ? maxDate.toISOString().split('T')[0]
    : new Date().toISOString().split('T')[0];

  // Wrapper 클래스
  const wrapperClasses = classNames(
    'flex flex-col gap-3',
    {
      'w-full': fullWidth,
    },
    className
  );

  // Input 클래스
  const inputClasses = classNames(
    'px-4 py-3 rounded-[10px] transition-all duration-200',
    'border-[1.5px] text-[15px] font-medium',
    'outline-none',
    {
      'border-[#E6E9F5] focus:border-[#4B7BF5]': !hasError && !disabled,
      'border-[#FF5E71]': hasError,
      'border-[#E6E9F5] bg-[#F2F4FC] text-[#AFB2C2] cursor-not-allowed': disabled,
    }
  );

  // 라벨 클래스
  const labelClasses = 'text-sm font-medium text-[#222638] mb-1';

  // 힌트 클래스
  const hintClasses = 'text-xs text-[#AFB2C2]';

  // 차이 표시 클래스
  const diffClasses = 'text-sm text-[#6B7280]';

  // 에러 메시지 클래스
  const errorClasses = 'text-xs text-[#FF5E71]';

  return (
    <div className={wrapperClasses}>
      {/* Label */}
      {label && <label className={labelClasses}>{label}</label>}

      {/* Date/Time Inputs */}
      <div className="flex gap-3 flex-wrap">
        {/* Date Input */}
        <input
          type="date"
          value={dateValue}
          onChange={handleDateChange}
          max={maxDateStr}
          disabled={disabled}
          className={inputClasses}
        />

        {/* Time Input (conditional) */}
        {needTimeInput && (
          <input
            type="time"
            value={timeValue}
            onChange={handleTimeChange}
            disabled={disabled}
            className={inputClasses}
          />
        )}
      </div>

      {/* Difference Display */}
      {dateValue && timeValue && (
        <div>
          {!isFuture ? (
            <span className={diffClasses}>
              {hourDiff < 24 ? `${hourDiff} hours` : `${daysDiff} days`}
            </span>
          ) : (
            <span className={errorClasses}>Should be in the past</span>
          )}
        </div>
      )}

      {/* Hint */}
      {hint && !hasError && <span className={hintClasses}>{hint}</span>}

      {/* Error Message */}
      {hasError && errorMessage && (
        <span className={errorClasses}>{errorMessage}</span>
      )}
    </div>
  );
}

export default memo(V2QsOnsetSimple);
